// What went wrong with a submission, in the alert's words (DESIGN_SPEC §11.2 error state; IMPL_ADDENDUM §2a/2b).
// Errors are ink, never red: "× Couldn't submit · 403 from /v1/jobs: Cloudflare rejected the check (…) · Check again".
//   describeSubmitError(err, kind) → { head, detail, action }
//   describeRejected(reason, size) · describeReadFailure(message)
//   refang(text) · normalizeUrl(text) · pastedUrl(text)    URL input helpers (hxxp://host[.]test → http://host.test)
import { isApiError } from "@/lib/api";
import { bytes } from "@/lib/format";

type SubmitAction = "retry" | "choose" | "signin" | "check" | "recheck" | "edit-url" | "analyses";

export type SubmitProblem = { head: string; detail: string; action: SubmitAction | null };

const TURNSTILE_CODES: Record<string, string> = {
  "missing-input-response": "no check token was sent",
  "invalid-input-response": "the check token was not valid",
  "timeout-or-duplicate": "the check token had expired or was already used",
  "invalid-input-secret": "the controller's Turnstile secret is wrong",
  "missing-input-secret": "the controller has no Turnstile secret",
  "bad-request": "Cloudflare rejected the verification request",
  "internal-error": "Cloudflare had an internal error",
};

function turnstileWhy(body: unknown): string {
  const codes = body && typeof body === "object" ? (body as { error_codes?: unknown }).error_codes : undefined;
  const list = Array.isArray(codes) ? codes.filter((c): c is string => typeof c === "string") : [];
  if (!list.length) return "Cloudflare rejected the check";
  const known = list.map((c) => TURNSTILE_CODES[c]).filter(Boolean);
  return known.length ? `Cloudflare rejected the check: ${known.join("; ")}` : `Cloudflare rejected the check (${list.join(", ")})`;
}

// POST /v1/jobs/bulk over the per-user quota: 429 {code:"quota_exceeded", quota, period, used}
function quotaWhy(body: unknown): string {
  const b = body && typeof body === "object" ? (body as { quota?: unknown; period?: unknown; used?: unknown }) : {};
  const quota = typeof b.quota === "number" ? b.quota : null;
  const period = typeof b.period === "string" ? b.period.replace(/^(\d+)h$/, "$1 h") : "24 h";
  const used = typeof b.used === "number" ? b.used : null;
  const limit = quota != null ? `${quota} bulk submissions per ${period}` : `bulk submissions are limited per ${period}`;
  return `${limit}${used != null ? ` · ${used} used` : ""} · submit the files one at a time, or try again later`;
}

export function describeSubmitError(err: unknown, kind: "file" | "url" | "batch"): SubmitProblem {
  if (isApiError(err)) {
    const from = `${err.status} from ${err.path || "/v1/jobs"}`;
    const detail = err.detail;
    if (err.status === 401) return { head: "Sign in to submit", detail: `${from}: ${detail || "login required"}`, action: "signin" };
    if (err.status === 403 && /turnstile/i.test(detail)) return { head: "Couldn't submit", detail: `${from}: ${turnstileWhy(err.body)}`, action: "check" };
    if (err.status === 502 && /turnstile/i.test(detail))
      return { head: "Couldn't submit", detail: `${from}: the controller couldn't reach Cloudflare to verify the check; nothing was queued`, action: "retry" };
    if (err.status === 429) return { head: "Bulk limit reached", detail: quotaWhy(err.body), action: null };
    if (err.status === 413) return { head: "Couldn't submit", detail: `${from}: the upload is larger than the server in front of the controller accepts`, action: "choose" };
    if (err.status === 400) return { head: "Couldn't submit", detail: `${from}: ${detail || err.statusText}`, action: kind === "url" ? "edit-url" : "choose" };
    return { head: "Couldn't submit", detail: `${from}: ${detail || err.statusText || "no detail"}`, action: "retry" };
  }
  const msg = err instanceof Error ? err.message : String(err);
  if (err instanceof TypeError) return { head: "Couldn't submit", detail: `no answer from the controller (${msg || "network error"})`, action: "retry" };
  return { head: "Couldn't submit", detail: msg || "unknown error", action: "retry" };
}

export function describeRejected(reason: "empty" | "too-large", size: number): SubmitProblem {
  return reason === "empty"
    ? { head: "Can't submit this file", detail: `it is empty (0${" "}B); the controller needs the sample's bytes`, action: "choose" }
    : { head: "Can't submit this file", detail: `it is ${bytes(size)}; the controller accepts up to 512${" "}MiB`, action: "choose" };
}

export function describeReadFailure(message: string): SubmitProblem {
  return { head: "Couldn't read the file", detail: message, action: "choose" };
}

/** Undo common defanging: hxxp(s), [.] (.) {.} [dot], [:]. */
export function refang(text: string): string {
  return text
    .trim()
    .replace(/^hxxp(s?)(\[?:\]?|:)/i, (_, s: string) => `http${s}:`)
    .replace(/\[\.\]|\(\.\)|\{\.\}|\[dot\]|\(dot\)/gi, ".")
    .replace(/\[:\]/g, ":");
}

/** An http(s) URL for the guest's browser, or null. A bare host gets http:// (the browser upgrades). */
export function normalizeUrl(input: string): string | null {
  const t = refang(input);
  if (!t || /\s/.test(t)) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(t) ? t : /^[a-z][a-z0-9+.-]*:/i.test(t) && !/^[^:/]+:\d/.test(t) ? null : `http://${t}`;
  if (!withScheme) return null;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    const host = u.hostname;
    if (!host || !(host.includes(".") || host === "localhost" || host.startsWith("["))) return null;
    return withScheme;
  } catch {
    return null;
  }
}

/** Text pasted anywhere on the page counts as a URL only with a scheme or www. (a file name like a.exe must not). */
export function pastedUrl(text: string): string | null {
  const t = refang(text);
  if (!/^(https?:\/\/|www\.)/i.test(t)) return null;
  return normalizeUrl(t);
}
